import { useCallback, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { useNavigate } from 'react-router-dom';
import A_Button from '@/components/A_Atoms/A_Button/A_Button';
import A_CircleButton from '@/components/A_Atoms/A_CircleButton/A_CircleButton';
import A_ChatAvatar from '@/components/A_Atoms/A_ChatAvatar/A_ChatAvatar';
import A_ToggleButton from '@/components/A_Atoms/A_ToggleButton/A_ToggleButton';
import M_ChatUserRow from '@/components/M_Molecules/M_ChatUserRow/M_ChatUserRow';
import { Q_Icon } from '@/components/Q_Quarks/Q_Icon/Q_Icon';
import { ChatUser, Conversation, chatApi } from '@/services/chatApi';
import { usersApi } from '@/services/usersApi';
import { PATHS } from '@/constants/paths';
import { getTranslation } from '@/constants/translations';
import { useLanguage } from '@/contexts/LanguageContext';
import { copyToClipboard } from '@/utils/clipboard';
import { conversationAvatar, conversationTitle } from '@/utils/chat';
import styles from './S_ChatMembersPopup.module.css';

interface S_ChatMembersPopupProps {
  conversation: Conversation;
  currentUserId?: string;
  onClose: () => void;
  onUpdated: (conversation: Conversation) => void;
  onLeft: (conversationId: string) => void;
}

const SEARCH_DEBOUNCE_MS = 300;
const COPIED_FEEDBACK_MS = 2000;

/** Ссылку показываем на текущем домене — она должна открываться там же, где чат. */
const inviteLinkFor = (token: string): string =>
  `${window.location.origin}${PATHS.CHAT_INVITE_BY_TOKEN(token)}`;

export default function S_ChatMembersPopup({
  conversation,
  currentUserId,
  onClose,
  onUpdated,
  onLeft,
}: S_ChatMembersPopupProps) {
  const { language } = useLanguage();
  const t = useCallback(
    (key: string) => getTranslation(language, key),
    [language]
  );
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ChatUser[]>([]);
  const [inviteLink, setInviteLink] = useState('');
  const [isCopied, setIsCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMediatorEnabled, setIsMediatorEnabled] = useState<boolean | null>(
    null
  );
  //  На сервере медиатора может не быть вовсе — тогда не показываем настройку.
  const [isMediatorAvailable, setIsMediatorAvailable] = useState(false);

  const isGroup = conversation.kind === 'group';
  const canManage =
    conversation.myRole === 'owner' || conversation.myRole === 'admin';
  const members = useMemo(
    () => conversation.members || [],
    [conversation.members]
  );
  const memberIds = useMemo(
    () => new Set(members.map(member => member.user.id)),
    [members]
  );

  useEffect(() => {
    const normalizedQuery = query.trim();
    if (!canManage || !normalizedQuery) {
      setResults([]);
      return;
    }

    let isActive = true;
    const timeoutId = setTimeout(() => {
      usersApi
        .searchUsers({ q: normalizedQuery, limit: 20 })
        .then(response => {
          if (!isActive) return;
          setResults(
            (response.data?.users || [])
              .map(user => ({
                id: String(user.id),
                name: user.name || '',
                username: user.username || user.handle || '',
                avatar: user.avatar,
              }))
              .filter(user => !memberIds.has(user.id))
          );
        })
        .catch(() => {
          if (isActive) setResults([]);
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      isActive = false;
      clearTimeout(timeoutId);
    };
  }, [canManage, memberIds, query]);

  useEffect(() => {
    if (!isGroup) return;

    let isActive = true;
    chatApi
      .getMediatorState(conversation.id)
      .then(response => {
        if (!isActive) return;
        setIsMediatorEnabled(Boolean(response.data?.isEnabled));
        setIsMediatorAvailable(Boolean(response.data?.isAvailable));
      })
      .catch(() => {
        if (isActive) setIsMediatorEnabled(null);
      });

    return () => {
      isActive = false;
    };
  }, [conversation.id, isGroup]);

  const toggleMediator = useCallback(async () => {
    try {
      const response = await chatApi.setMediatorEnabled(
        conversation.id,
        !isMediatorEnabled
      );
      setIsMediatorEnabled(Boolean(response.data?.isEnabled));
    } catch {
      setError(t('chat.mediator.toggleError'));
    }
  }, [conversation.id, isMediatorEnabled, t]);

  const addMember = useCallback(
    async (user: ChatUser) => {
      try {
        const response = await chatApi.addMembers(conversation.id, [user.id]);
        if (response.data?.conversation) {
          onUpdated(response.data.conversation);
          setQuery('');
        }
      } catch {
        setError(t('chat.errors.memberAdd'));
      }
    },
    [conversation.id, onUpdated, t]
  );

  const removeMember = useCallback(
    async (userId: string) => {
      try {
        const response = await chatApi.removeMember(conversation.id, userId);
        if (userId === currentUserId) {
          onLeft(conversation.id);
          return;
        }
        if (response.data?.conversation) {
          onUpdated(response.data.conversation);
        }
      } catch {
        setError(t('chat.errors.memberRemove'));
      }
    },
    [conversation.id, currentUserId, onLeft, onUpdated, t]
  );

  const shareInviteLink = useCallback(async () => {
    try {
      const response = await chatApi.getInviteLink(conversation.id);
      const token = response.data?.invite.token;
      if (!token) return;

      const link = inviteLinkFor(token);
      setInviteLink(link);
      await copyToClipboard(link);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), COPIED_FEEDBACK_MS);
    } catch {
      setError(t('chat.errors.inviteLink'));
    }
  }, [conversation.id, t]);

  const title = conversationTitle(conversation, t('chat.list.untitled'));

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={styles.wrapper}
        onClick={event => event.stopPropagation()}
      >
        <div className={styles.header}>
          <h4 className={styles.title}>{t('chat.details.title')}</h4>
          <A_CircleButton
            variant='transparent'
            size='small'
            icon={<Q_Icon.CrossIcon width={20} height={20} fill='none' />}
            onClick={onClose}
          />
        </div>

        <div className={styles.summary}>
          <A_ChatAvatar
            title={title}
            src={conversationAvatar(conversation)}
            isGroup={isGroup}
            size={64}
          />
          <div className={styles.summary_text}>
            <span className={cn(styles.summary_title, 'text-p-6')}>
              {title}
            </span>
            <span className={cn(styles.summary_caption, 'text-caption')}>
              {isGroup
                ? `${conversation.memberCount} ${t('chat.details.membersSuffix')}`
                : `@${conversation.peer?.username || ''}`}
            </span>
          </div>
        </div>

        {isGroup && isMediatorAvailable && (
          <div className={styles.section}>
            <span className={cn(styles.section_title, 'text-caption')}>
              {t('chat.mediator.sectionTitle')}
            </span>
            <p className={cn(styles.mediator_hint, 'text-caption')}>
              {t('chat.mediator.sectionHint')}
            </p>
            <div className={styles.mediator_row}>
              {canManage ? (
                <A_ToggleButton
                  isActive={Boolean(isMediatorEnabled)}
                  onClick={toggleMediator}
                  bgColor='white'
                  title={
                    isMediatorEnabled
                      ? t('chat.mediator.on')
                      : t('chat.mediator.off')
                  }
                />
              ) : (
                <span className={cn(styles.mediator_hint, 'text-caption')}>
                  {isMediatorEnabled
                    ? t('chat.mediator.on')
                    : t('chat.mediator.off')}
                </span>
              )}
            </div>
          </div>
        )}

        {isGroup && canManage && (
          <div className={styles.section}>
            <span className={cn(styles.section_title, 'text-caption')}>
              {t('chat.details.inviteLink')}
            </span>
            <div className={styles.invite_row}>
              <span className={cn(styles.invite_value, 'text-caption')}>
                {inviteLink || t('chat.details.inviteLinkHint')}
              </span>
              <A_Button
                variant='secondary'
                size='small'
                fontSize='text-p-4'
                showIcon
                icon={<Q_Icon.CopyIcon width={20} height={20} fill='none' />}
                onClick={shareInviteLink}
              >
                {isCopied
                  ? t('chat.details.linkCopied')
                  : t('chat.details.copyLink')}
              </A_Button>
            </div>
          </div>
        )}

        {isGroup && canManage && (
          <div className={styles.section}>
            <span className={cn(styles.section_title, 'text-caption')}>
              {t('chat.details.addMembers')}
            </span>
            <input
              type='text'
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder={t('chat.new.searchPlaceholder')}
              className={cn(styles.input, 'text-p-8')}
            />
            {results.length > 0 && (
              <div className={styles.results}>
                {results.map(user => (
                  <M_ChatUserRow
                    key={user.id}
                    user={user}
                    onClick={addMember}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {isGroup && (
          <div className={styles.section}>
            <span className={cn(styles.section_title, 'text-caption')}>
              {t('chat.details.members')}
            </span>
            <div className={styles.results}>
              {members.map(member => (
                <M_ChatUserRow
                  key={member.user.id}
                  user={member.user}
                  caption={
                    member.role === 'member'
                      ? undefined
                      : t(`chat.details.role.${member.role}`)
                  }
                  action={
                    canManage &&
                    member.role !== 'owner' &&
                    member.user.id !== currentUserId ? (
                      <button
                        type='button'
                        className={styles.remove}
                        onClick={() => removeMember(member.user.id)}
                        aria-label={t('chat.details.removeMember')}
                      >
                        <Q_Icon.CrossIcon width={16} height={16} fill='none' />
                      </button>
                    ) : undefined
                  }
                />
              ))}
            </div>
          </div>
        )}

        {!isGroup && conversation.peer && (
          <A_Button
            variant='secondary'
            size='small'
            fontSize='text-p-4'
            width='100%'
            onClick={() =>
              navigate(
                PATHS.USER_PROFILE_BY_HANDLE(conversation.peer?.username || '')
              )
            }
          >
            {t('chat.details.openProfile')}
          </A_Button>
        )}

        {error && <p className={cn(styles.error, 'text-caption')}>{error}</p>}

        {isGroup && (
          <A_Button
            variant='tertiary'
            size='small'
            fontSize='text-p-4'
            width='100%'
            onClick={() => currentUserId && removeMember(currentUserId)}
          >
            {t('chat.details.leaveGroup')}
          </A_Button>
        )}
      </div>
    </div>
  );
}
